import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface AppPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppPromoModal: React.FC<AppPromoModalProps> = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownloadApp = () => {
    // Google Play Store URL for Android app
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.cccadamccc.SuiviFinance';
    window.open(playStoreUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-end p-2 sm:p-4 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
      onClick={handleBackdropClick}
    >
      <div
        className={`relative bg-dark-800 rounded-2xl shadow-2xl w-full max-w-[480px] max-h-[95vh] flex flex-col transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{ padding: '20px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Fixed position */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-dark-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-1 z-10 bg-dark-800"
          aria-label="Fermer la modale"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 pr-1" style={{ maxHeight: 'calc(95vh - 40px)' }}>
          {/* Title */}
          <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6">
            Découvrez l'application mobile SuiviFinance
          </h2>

          {/* Video Block */}
          <div className="mb-4 sm:mb-6 rounded-xl overflow-hidden bg-dark-700">
            <video
              src="/videos/suivifinance-app-promo.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto rounded-xl"
              style={{ borderRadius: '12px', maxHeight: '250px', objectFit: 'contain' }}
            />
          </div>

          {/* Marketing Paragraph */}
          <p className="text-dark-400 text-center mb-4 sm:mb-6 text-xs sm:text-sm leading-relaxed">
            Gérez vos dépenses encore plus facilement grâce à notre application mobile. Un accès instantané à votre solde, vos graphiques, vos rappels et à toutes vos fonctionnalités préférées — directement dans votre poche.
          </p>

          {/* Key Bullets */}
          <div className="space-y-1 mb-4 sm:mb-6">
            <div className="flex items-center justify-center text-dark-300 text-xs sm:text-sm">
              ⚡ Ajoutez vos dépenses en 1 seconde
            </div>
            <div className="flex items-center justify-center text-dark-300 text-xs sm:text-sm">
              📊 Visualisez vos graphiques en un clin d'œil
            </div>
            <div className="flex items-center justify-center text-dark-300 text-xs sm:text-sm">
              🔔 Recevez des rappels intelligents
            </div>
          </div>

          {/* Google Play Store Button */}
          <div className="mb-3 sm:mb-4 flex justify-center">
            <a
              href="https://play.google.com/store/apps/details?id=com.cccadamccc.SuiviFinance"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDownloadApp}
              className="inline-block hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
            >
              <img
                src="/images/GooglePlay.png"
                alt="Télécharger sur Google Play"
                className="h-auto max-w-full"
                style={{ maxHeight: '50px' }}
              />
            </a>
          </div>

          {/* Secondary Link */}
          <button
            onClick={onClose}
            className="w-full text-center text-xs sm:text-sm text-dark-400 hover:text-white transition-colors focus:outline-none mb-2"
          >
            Continuer sur la version web
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppPromoModal;

